import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  ComponentLoader,
  DesignedModal,
  GeneralSelect,
  Toast,
} from '@clickcease/cc-client-components-lib'

import { Trans, useTranslation } from 'react-i18next'
import {
  GoogleTagManagerDesc,
  GoogleTagManagerError,
  GoogleTagManagerModalSelectWrapper,
  GoogleTagManagerModalStyled,
  ToasterWrapper,
} from 'Features/domainsManagement/components/GetTagModal/components/Instructions/GoogleTagManager/GoogleTagManagerModal/GoogleTagManagerModal.styled'
import { useGetGtmAccounts } from 'Features/domainsManagement/components/GetTagModal/components/Instructions/GoogleTagManager/hooks/useGetGtmAccounts'
import { useGetGtmAccountContainers } from 'Features/domainsManagement/components/GetTagModal/components/Instructions/GoogleTagManager/hooks/useGetGtmAccountContainers'
import { useAddTag } from 'Features/domainsManagement/components/GetTagModal/components/Instructions/GoogleTagManager/hooks/useAddCeTag'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useGetErrorText } from 'Utils/errorText'
import { Domain } from 'Types/Domain'
import { queryClient } from 'Lib/react-query'
import { SelectOption } from 'Types/SelectOption'
import { useGetTagValidation } from 'Features/validateTag/api/getTagValidation'

export const GoogleTagManagerModal = ({
  isOpen,
  setIsOpen,
  loading,
  googleToken,
  domain,
}: {
  isOpen: boolean
  setIsOpen: (val: boolean, publish: boolean) => void
  loading?: boolean
  googleToken: string
  domain?: Domain
}) => {
  const { t } = useTranslation('domainManagement')
  const [error, setError] = useState<string>('')
  const [gtmAccount, setGtmAccount] = useState<SelectOption>()
  const [gtmContainer, setGtmContainer] = useState<SelectOption>()
  const [publishLoading, setPublishLoading] = useState<boolean>(false)
  const [alreadyExists, setAlreadyExists] = useState<{ account?: string; container?: string }>({
    account: '',
    container: '',
  })
  const accountsQuery = useGetGtmAccounts(googleToken)
  const containersQuery = useGetGtmAccountContainers(googleToken, gtmAccount?.value || '')
  const { mutate, isLoading } = useAddTag()
  const { accountId } = useUserStore()
  const { getErrorText } = useGetErrorText()
  const { data: tagValidated } = useGetTagValidation({ domainId: domain?.id || 0 })

  const domainHasData = useMemo(() => {
    if (!(tagValidated instanceof Error)) {
      return tagValidated?.data?.protected
    }
  }, [tagValidated])

  //preselect gtm account if it's already set
  useEffect(() => {
    const gtmAccount = accountsQuery?.data?.find(
      (account) => account.accountId === domain?.gtmAccountId
    )
    if (gtmAccount) {
      setGtmAccount({ value: gtmAccount?.accountId, label: gtmAccount?.name })
      setAlreadyExists({ ...alreadyExists, account: gtmAccount?.accountId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsQuery.data, domain])

  //preselect gtm container if it's already set
  useEffect(() => {
    const gtmContainer = containersQuery?.data?.container.find(
      (container) => container.containerId === domain?.gtmContainerId
    )
    if (gtmContainer) {
      setGtmContainer({ value: gtmContainer?.containerId, label: gtmContainer?.name })
      setAlreadyExists({ ...alreadyExists, container: gtmContainer?.containerId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containersQuery.data, domain])

  const gtmAccountsOptions = useMemo(() => {
    if (accountsQuery.data) {
      return accountsQuery.data?.map((account): { label: string; value: string } => {
        return {
          value: account.accountId,
          label: account.name,
        }
      })
    } else {
      return []
    }
  }, [accountsQuery.data])

  const gtmContainersOptions = useMemo(() => {
    if (containersQuery.data) {
      return containersQuery.data?.container.map(
        (containerInstance): { label: string; value: string } => {
          return {
            value: containerInstance.containerId,
            label: containerInstance.name,
          }
        }
      )
    } else {
      return []
    }
  }, [containersQuery.data])

  const accountChanged = (item: SelectOption) => {
    setGtmAccount(item)
    setError('')
  }

  const containerChanged = (item: SelectOption) => {
    setGtmContainer(item)
    setError('')
  }

  const addTag = (publish: boolean) => {
    if (publish) {
      setPublishLoading(true)
    }
    mutate(
      {
        accountId,
        domainId: domain?.id || 0,
        token: googleToken,
        gtmContainerId: gtmContainer?.value || '',
        gtmAccountId: gtmAccount?.value || '',
        publish: publish || false,
      },
      {
        onError: (error) => {
          setPublishLoading(false)
          setError(getErrorText(error) || '')
        },
        onSuccess: (res) => {
          setPublishLoading(false)
          if (!(res instanceof Error)) {
            void queryClient.invalidateQueries(['domains'])
            setIsOpen(true, publish)
          }
        },
      }
    )
  }

  const closeModal = () => {
    setIsOpen(false, false)
  }

  const accountAndContainerSelected =
    alreadyExists?.container === gtmContainer?.value && alreadyExists?.account === gtmAccount?.value
  const showHasDataToast = domainHasData && !domain?.gtmImplemented && accountAndContainerSelected

  return (
    <DesignedModal
      isOpen={isOpen}
      title={t('gtm_auto.connect_gtm')}
      loading={loading}
      disableAutoFocus
      onOpenChange={closeModal}
      style={{ width: '500px' }}
      actions={
        <>
          <Button
            text={t('gtm_auto.cancel')}
            variant="ghost"
            onClick={() => setIsOpen(false, false)}
          />
          <Button
            text={t('gtm_auto.submit_tag')}
            loading={isLoading && !publishLoading}
            disabled={isLoading || !gtmContainer?.value || accountAndContainerSelected}
            onClick={() => addTag(false)}
            variant={'outline'}
          />
          <Button
            text={t('gtm_auto.publish_tag')}
            loading={isLoading && publishLoading}
            disabled={isLoading || !gtmContainer?.value || accountAndContainerSelected}
            onClick={() => addTag(true)}
          />
        </>
      }
    >
      <GoogleTagManagerModalStyled>
        {accountsQuery.isLoading && <ComponentLoader />}
        {gtmAccountsOptions.length > 0 && (
          <>
            <GoogleTagManagerDesc>
              {t('gtm_auto.select_account_and_container')}
            </GoogleTagManagerDesc>
            <GoogleTagManagerModalSelectWrapper>
              <GeneralSelect
                topTitle
                wrapperCSS={{
                  '> div:nth-child(1)': {
                    fontWeight: '600',
                  },
                }}
                title={t('gtm_auto.select_account')}
                value={gtmAccount}
                options={gtmAccountsOptions}
                onChange={(item) => {
                  accountChanged(item)
                }}
              />
            </GoogleTagManagerModalSelectWrapper>
          </>
        )}
        {!accountsQuery.isLoading && gtmAccountsOptions && gtmAccountsOptions.length === 0 && (
          <GoogleTagManagerError>{t('gtm_auto.no_accounts_found')}</GoogleTagManagerError>
        )}

        {gtmContainersOptions.length > 0 && gtmAccount && (
          <GoogleTagManagerModalSelectWrapper>
            <GeneralSelect
              topTitle
              title={t('gtm_auto.select_container')}
              value={gtmContainer}
              wrapperCSS={{
                '> div:nth-child(1)': {
                  fontWeight: '600',
                },
              }}
              options={gtmContainersOptions}
              onChange={(item) => containerChanged(item)}
            />
          </GoogleTagManagerModalSelectWrapper>
        )}
        <ToasterWrapper>
          {showHasDataToast && (
            <Toast type="success">
              <Trans
                ns={'domainManagement'}
                i18nKey={'gtm_auto.domain_has_data'}
                components={{ 1: <span style={{ fontWeight: '600', display: 'contents' }} /> }}
              />
            </Toast>
          )}
          {!showHasDataToast && gtmAccount && gtmContainer && (
            <Toast type="warning">
              <Trans
                ns={'domainManagement'}
                i18nKey={'gtm_auto.submit_info'}
                components={{ 1: <span style={{ fontWeight: '600', display: 'contents' }} /> }}
              />
            </Toast>
          )}
        </ToasterWrapper>
        <GoogleTagManagerError>{error}</GoogleTagManagerError>
      </GoogleTagManagerModalStyled>
    </DesignedModal>
  )
}
